import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { isClickedOutside } from "@theme/utilities";

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | undefined} */
  #mo;

  /** @type {boolean} */
  #wasOpened = false;

  /** @type {boolean} */
  #reloadQueued = false;

  /** @type {boolean} */
  #userCloseIntent = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Detect user backdrop/Escape intent at capture, to trigger reload on dialog close
    /** @param {MouseEvent} e */
    const onClickCapture = (e) => {
      const dialog = this.refs?.dialog;
      if (!dialog?.open) return;
      if (!(e.target instanceof Element)) return;
      if (isClickedOutside(e, dialog)) {
        this.#userCloseIntent = true;
      }
    };
    this.addEventListener("click", onClickCapture, { capture: true });

    /** @param {KeyboardEvent} e */
    const onKeydownCapture = (e) => {
      const dialog = this.refs?.dialog;
      if (!dialog?.open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        this.#userCloseIntent = true;
      }
    };
    this.addEventListener("keydown", onKeydownCapture, { capture: true });

    // When dialog fully closes, reload if user-initiated via backdrop/Escape
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Fallback: ensure close buttons inside the component close the dialog
    /** @param {Event} e */
    const delegateClose = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const isCloseButton =
        target.closest(".cart-drawer__close-button") != null;

      if (!isCloseButton) return;

      e.preventDefault();
      e.stopPropagation();

      this.#handleUserClose();
    };

    this.addEventListener("click", delegateClose, { capture: true });
    this.addEventListener("pointerup", delegateClose, { capture: true });
    this.addEventListener("mousedown", delegateClose, { capture: true });

    // Direct handlers on close buttons
    const attachHandlers = () => {
      this.querySelectorAll(".cart-drawer__close-button").forEach((btn) => {
        if (!(btn instanceof HTMLElement) || btn.dataset.closeHandlerAttached)
          return;

        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#handleUserClose();
        };

        btn.addEventListener("click", handler);
        btn.addEventListener("pointerup", handler);
        btn.addEventListener("mousedown", handler);
        btn.dataset.closeHandlerAttached = "true";
      });
    };

    attachHandlers();
    this.#mo = new MutationObserver(attachHandlers);
    this.#mo.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    try {
      this.#mo?.disconnect();
    } catch (_) {}
  }

  /** @type {(event: CustomEvent<any>) => void} */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute("auto-open")) return;

    const source = event?.detail?.data?.source;
    const didError = event?.detail?.data?.didError;
    if (didError) return;
    if (source !== "product-form-component") return;

    document.querySelectorAll("cart-drawer-component").forEach((el) => {
      if (el !== this && el instanceof CartDrawerComponent) {
        try {
          el.close();
        } catch (_) {}
      }
    });

    this.#wasOpened = true;
    this.#reloadQueued = false;
    this.showDialog();
  };

  open() {
    document.querySelectorAll("cart-drawer-component").forEach((el) => {
      if (el !== this && el instanceof CartDrawerComponent) {
        try {
          el.close();
        } catch (_) {}
      }
    });

    this.#wasOpened = true;
    this.#reloadQueued = false;
    this.showDialog();

    customElements.whenDefined("shopify-payment-terms").then(() => {
      const root = document.querySelector("shopify-payment-terms")?.shadowRoot;
      const cta = root?.querySelector("#shopify-installments-cta");

      cta?.addEventListener("click", () => this.#handleUserClose(), {
        once: true,
      });
    });
  }

  close() {
    this.closeDialog();
  }

  // ✅ SINGLE SOURCE OF TRUTH
  #handleUserClose() {
    if (!this.#wasOpened || this.#reloadQueued) {
      try {
        this.closeDialog();
      } catch (_) {}
      return;
    }

    this.#reloadQueued = true;

    try {
      this.closeDialog();
    } catch (_) {}

    // Let Horizon tear down, then hard reset page
    setTimeout(() => {
      window.location.reload();
    }, 80);
  }

  /** Reload when the dialog closes via backdrop/Escape user intent */
  #handleDialogClose = () => {
    if (!this.#wasOpened) return;
    if (this.#reloadQueued) return;
    if (!this.#userCloseIntent) return;

    this.#reloadQueued = true;
    this.#userCloseIntent = false;
    setTimeout(() => {
      window.location.reload();
    }, 80);
  };
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
